import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../constants/routes';
import { PLAY_STORE_URL } from '../../constants';
import Button from '../../components/ui/Button';
import SEO from '../../components/common/SEO';
import { MailIcon, SmartphoneIcon, ChurchIcon } from '../../components/common/Icons';

const FAQ_CATEGORIES = ['All Questions', 'Getting Started', 'Leagues & Hosting', 'Translations & Privacy'];

const FAQS = [
  {
    category: 'Getting Started',
    q: 'How do I download and play Bible Contest?',
    a: 'You can download the official Bible Contest app for Android on the Google Play Store. Simply search for "Bible Contest" or use our direct download link on the homepage. Once installed, sign in with your email or Google account to start playing daily quizzes immediately.'
  },
  {
    category: 'Leagues & Hosting',
    q: 'How do Student Leagues and Prize Sponsorships work?',
    a: 'We organize dedicated student leagues for churches, Christian schools, and youth ministries. Contestants answer timed Scripture questions throughout the contest season. Top scorers receive sponsored prizes such as Study Bibles, trophies, and certificates of accomplishment.'
  },
  {
    category: 'Leagues & Hosting',
    q: 'Can our church or Sunday school host a private tournament?',
    a: 'Yes! Our platform supports customized seasons with tailored quiz schedules and private leaderboards for your local church congregation or youth group. Reach out to us via Support Chat or through our Volunteer/Careers page to get set up with Host Studio access.'
  },
  {
    category: 'Translations & Privacy',
    q: 'Why is live support chat restricted to signed-in users?',
    a: 'Requiring an authenticated session protects contestant privacy, prevents spam, and ensures that our admin support team can follow up with you seamlessly across all your devices. Guests can still submit inquiries directly through our chat form!'
  },
  {
    category: 'Translations & Privacy',
    q: 'What Bible translations are used in quiz questions?',
    a: 'Questions are verified against standard, faithful translations including ESV, NIV, KJV, and regional language translations. Each answer includes the exact Scripture book, chapter, and verse reference for personal study and devotional review.'
  }
];

const SupportHelp = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All Questions');
  const [faqSearch, setFaqSearch] = useState('');
  const [copiedEmail, setCopiedEmail] = useState('');

  const handleCopyEmail = (email) => {
    navigator.clipboard?.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(''), 2200);
  };

  const filteredFaqs = FAQS.filter((faq) => {
    const matchesCat = selectedCategory === 'All Questions' || faq.category === selectedCategory;
    const matchesSearch = !faqSearch.trim() ||
      faq.q.toLowerCase().includes(faqSearch.toLowerCase()) ||
      faq.a.toLowerCase().includes(faqSearch.toLowerCase());
    return matchesCat && matchesSearch;
  });

  // Generate Schema.org FAQPage structured data
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': FAQS.map((faq) => ({
      '@type': 'Question',
      'name': faq.q,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.a
      }
    }))
  };

  return (
    <div className="support-page" style={{ width: '100%' }}>
      <SEO
        title="Support, FAQs & Community Care — Bible Contest"
        description="Find answers to frequently asked questions about Bible Contest, hosting church leagues, student prize sponsorships, and contacting our ministry team."
        keywords="Bible contest support, Bible quiz FAQ, host church league help, Christian quiz app support, student scripture league"
        canonicalUrl="/support"
        schema={faqSchema}
      />

      {/* Hero Header */}
      <section className="support-hero">
        <span className="hero-pill">🤝 Community Care & Ministry Support</span>
        <h1 className="support-hero__title">
          How Can We Serve & Encourage You?
        </h1>
        <p className="support-hero__subtitle">
          Find instant answers to frequently asked questions, or connect directly with our ministry team for personalized care, league setups, and prayer.
        </p>

        {/* Hero Help Center Search Bar */}
        <div className="support-hero-search-wrapper">
          <div className="support-hero-search-box">
            <svg
              className="support-hero-search-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search questions, leagues, rules, prizes, translations..."
              value={faqSearch}
              onChange={(e) => setFaqSearch(e.target.value)}
              className="support-hero-search-input"
              aria-label="Search frequently asked questions and help topics"
            />
            {faqSearch && (
              <button
                type="button"
                onClick={() => setFaqSearch('')}
                className="support-hero-search-clear"
                aria-label="Clear Search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Quick topic tags below search */}
          <div className="support-quick-tags">
            <span className="support-quick-tags__label">Popular:</span>
            {['Leagues', 'Prizes', 'Translations', 'Host Studio', 'Download'].map((topic) => (
              <button
                key={topic}
                type="button"
                className={`support-quick-tag-chip ${faqSearch === topic ? 'active' : ''}`}
                onClick={() => setFaqSearch(faqSearch === topic ? '' : topic)}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="faq-section">
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <h2 className="section-heading">Frequently Asked Questions</h2>
          <p className="section-subheading" style={{ marginBottom: '1.25rem' }}>
            Everything you need to know about playing quizzes, organizing church leagues, and scoring.
          </p>

          {/* Filter Chips on Mobile */}
          <div className="careers-filters" style={{ marginBottom: '1rem' }}>
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`filter-tab ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Active Search / Filter Feedback */}
          {faqSearch && (
            <div className="support-search-feedback">
              <span>Showing results for &ldquo;<strong>{faqSearch}</strong>&rdquo; ({filteredFaqs.length} found)</span>
              <button
                type="button"
                className="support-search-reset-btn"
                onClick={() => setFaqSearch('')}
              >
                Reset search
              </button>
            </div>
          )}
        </div>

        <div className="faq-list">
          {filteredFaqs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--clr-muted)', background: '#FFFFFF', borderRadius: '16px', border: '1px solid var(--clr-border)' }}>
              No questions found matching your filter.
            </div>
          ) : (
            filteredFaqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={index}
                  className={`faq-item ${isOpen ? 'open' : ''}`}
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                >
                  <div className="faq-header">
                    <h4 className="faq-question">{faq.q}</h4>
                    <div className="faq-toggle-circle">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="faq-body">
                      <p style={{ margin: 0 }}>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Additional Modern Contact Cards */}
      <section className="support-contact-grid">
        {/* Card 1: Direct Leadership & Official Channels */}
        <div className="support-contact-card">
          <div className="support-contact-card__icon" style={{ background: '#FFF4F3', color: 'var(--clr-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MailIcon size={24} style={{ color: 'var(--clr-primary)' }} />
          </div>
          <h4>Official Ministry Inquiries</h4>
          <p>Direct communication channels for contestants, pastors, and partners:</p>

          <div style={{ marginTop: 'auto' }}>
            <div className="support-email-pill">
              <div>
                <small style={{ display: 'block', color: 'var(--clr-muted)', fontSize: '10.5px' }}>Member Care & General</small>
                <code>support@biblecontest.app</code>
              </div>
              <button
                type="button"
                className="support-email-copy-btn"
                onClick={() => handleCopyEmail('support@biblecontest.app')}
              >
                {copiedEmail === 'support@biblecontest.app' ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            <div className="support-email-pill">
              <div>
                <small style={{ display: 'block', color: 'var(--clr-muted)', fontSize: '10.5px' }}>League Hosting & Churches</small>
                <code>manager@biblecontest.app</code>
              </div>
              <button
                type="button"
                className="support-email-copy-btn"
                onClick={() => handleCopyEmail('manager@biblecontest.app')}
              >
                {copiedEmail === 'manager@biblecontest.app' ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            <div className="support-email-pill">
              <div>
                <small style={{ display: 'block', color: 'var(--clr-muted)', fontSize: '10.5px' }}>Leadership & Sponsorships</small>
                <code>ceo@biblecontest.app</code>
              </div>
              <button
                type="button"
                className="support-email-copy-btn"
                onClick={() => handleCopyEmail('ceo@biblecontest.app')}
              >
                {copiedEmail === 'ceo@biblecontest.app' ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        {/* Card 2: Android Mobile App */}
        <div className="support-contact-card">
          <div className="support-contact-card__icon" style={{ background: 'rgba(5, 150, 105, 0.1)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SmartphoneIcon size={24} style={{ color: '#059669' }} />
          </div>
          <h4>Official Android App</h4>
          <p>Play live Scripture challenges, track your ranking, and receive daily alerts directly on your phone.</p>
          
          <div style={{ marginTop: 'auto', paddingTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
              <span style={{ color: '#F59E0B', fontSize: '14px' }}>★★★★★</span>
              <span style={{ fontSize: '12px', color: 'var(--clr-text-2)', fontWeight: 600 }}>100% Free · No Ads</span>
            </div>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn--primary"
              style={{ width: '100%', textAlign: 'center', justifyContent: 'center', textDecoration: 'none', minHeight: '44px' }}
            >
              Get on Google Play Store →
            </a>
          </div>
        </div>

        {/* Card 3: Host Studio Access */}
        <div className="support-contact-card">
          <div className="support-contact-card__icon" style={{ background: 'rgba(192, 133, 82, 0.14)', color: '#C08552', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChurchIcon size={24} style={{ color: '#C08552' }} />
          </div>
          <h4>Host Studio Access</h4>
          <p>Pastors, youth leaders, and Sunday school coordinators can access Host Studio to manage leagues and custom quizzes.</p>
          
          <div style={{ marginTop: 'auto', paddingTop: '10px' }}>
            <Button
              variant="secondary"
              size="md"
              style={{ width: '100%', minHeight: '44px' }}
              onClick={() => navigate(user ? ROUTES.PUBLIC.STUDIO : ROUTES.AUTH.LOGIN)}
            >
              {user ? 'Go to Host Studio →' : 'Host Sign In →'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportHelp;
